import pluralize from 'pluralize';

import { formatString, isDefined, isEmpty } from '@hsp/utilities';

import { strings } from 'constants/strings';
import { StringKeys } from 'types/constants/string.type';
import { IGetStringOptions } from 'types/utilities/index';
import { isDev } from 'utilities/is-dev';

/**
 * Use to retrieve a string form the strings library in a type safe way.
 * @param {StringKeys} key  The string to retrieve
 * @param {StringKeys[]} keys The string(s) used to replace the format placeholders.
 * @returns {string} A string from the library or null if not found.
 */
export const getFormattedString = (key: StringKeys, ...keys: StringKeys[]): string => {
  if (isEmpty(key)) {
    return '';
  }

  return formatString(getString(key), ...[...keys.map((stringKey) => getString(stringKey))]);
};

/**
 * Use to retrieve a string from the strings library in a type safe way.
 * @param {IGetStringOptions} options
 * @param {StringKeys} key  The string to retrieve
 * @returns {string} A string from the library.
 */
export const getString = (key: StringKeys | StringKeys[], options?: Partial<IGetStringOptions>): string => {
  if (isEmpty(key)) {
    return '';
  }

  const { casing, plural, prefix, separator, suffix } = {
    ...{
      casing: 'default',
      plural: false,
      prefix: '',
      separator: ' ',
      suffix: '',
    },
    ...options,
  } as IGetStringOptions;

  if (!Array.isArray(key)) {
    key = [key];
  }

  const stringValues = key.reduce((concatenatedValue: string[], currentKey: StringKeys) => {
    // reason: trouble determining what the type of previousValue should be, in this case
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const stringValue = currentKey.split('.').reduce((previousValue: any, currentValue) => {
      if (isEmpty(previousValue)) {
        return '';
      } else {
        const current = previousValue[currentValue];

        if (!isDefined(current) || isEmpty(current)) {
          return '';
        }

        if (typeof current === 'string') {
          switch (casing) {
            case 'lowercase':
              return current.toString().toLowerCase();

            case 'uppercase':
              return current.toString().toUpperCase();

            default:
              return current;
          }
        }

        return current;
      }
    }, strings);

    concatenatedValue.push(stringValue);

    return concatenatedValue;
  }, []);

  const value = stringValues.join(separator);

  return `${prefix}${plural ? pluralize(value) : value}${suffix}`;
};

/**
 *
 * @param key
 * @param options
 * @returns
 */
export const getTitle = (key?: StringKeys | StringKeys[], options?: Partial<IGetStringOptions>) => {
  const prefix = s('Site.HSP');
  const suffix = isDev ? formatString(' - {0}', s('Site.Beta', { casing: 'uppercase' })) : '';
  const title = isDefined(key) ? s(key, options) : undefined;

  return `${prefix}${isDefined(title) ? `: ${title}` : ''}${suffix}`;
};

// disabled this in order to provide shortcuts for using the getString & getTitle utility methods

// eslint-disable-next-line id-length
export const s = getString;
// eslint-disable-next-line id-length
export const t = getTitle;
